/**
 * Feature Flags para migração gradual do sistema RDO
 * 
 * Essas flags permitem ativar funcionalidades progressivamente
 * sem quebrar o sistema existente.
 */

export const rdoFlags = {
  // Fase 1: flag inerte. A normalização para o schema unificado deixou de
  // transformar o estado das páginas (RDO.tsx / PreenchendoRDO.tsx) e passou a
  // ocorrer apenas nas bordas (adapters por módulo). Mantida como falsa,
  // reservada para a migração completa das páginas (Fase 3).
  useUnifiedSchema: false,
  // Módulos LIBERADOS no RDO.tsx (rollout): cada um troca a seção legada pelo
  // módulo unificado via adapter. Cobertos por:
  // - testes de round-trip do normalizador (src/domain/rdo/__tests__)
  // - smoke test de renderização (src/components/rdo/__tests__/modules.smoke.test.tsx)
  // Para reverter rapidamente: troque para false aqui, ou desligue em runtime no
  // painel /dev/rdo-flags (somente DEV).
  useNewWorkforceInRDO: true,
  useNewMaterialsInRDO: true,
  useNewActivitiesInRDO: true,
  useNewPhotosInRDO: true,
  useNewVideosInRDO: true,
  useNewAttachmentsInRDO: true,
  useNewEquipmentsInRDO: true,
  // Ocorrências LIBERADA: módulo + adapter com paridade ao inline legado,
  // incluindo espelhamento das fotos no container geral de Fotos (onMirrorPhotos,
  // respeitando hidePhotos). Rollback via false aqui ou /dev/rdo-flags.
  useNewOccurrencesInRDO: true,
  useNewCommentsInRDO: true,
  useComposerInRDO: false,
} as const;

export type RDOFlags = typeof rdoFlags;

/**
 * Instruções de uso:
 * 
 * 1. useUnifiedSchema: (inerte na Fase 1) reservada para a migração completa
 *    das páginas ao schema unificado. Não altera o estado de rdoData hoje.
 * 2. useNewWorkforceInRDO: Ativa novo sistema de Mão de Obra no RDO
 * 3. useNewMaterialsInRDO: Ativa novo sistema de Materiais no RDO
 * 4. useNewActivitiesInRDO: Ativa novo sistema de Atividades no RDO
 * 5. useNewPhotosInRDO: Ativa novo sistema de Fotos no RDO
 * 6. useComposerInRDO: Migra para uso do RDOComposer unificado
 * 
 * Para os próximos passos, altere as flags conforme necessário.
 */

// Runtime flag overrides (sem reiniciar app)
export const rdoFlagOverrides: Partial<Record<keyof typeof rdoFlags, boolean>> = {};

export function getFlag<K extends keyof typeof rdoFlags>(key: K): boolean {
  return (rdoFlagOverrides[key] ?? rdoFlags[key]) as boolean;
}